<template>
	<article class="provider-progress-card">
		<header>
			<div class="provider-name">
				<span class="provider-status-icon" :class="progress.status">
					<AppIcon v-if="progress.status === ContactResearchProviderStatus.Success" :name="IconName.Check" :size="12" />
					<span
						v-else-if="
							progress.status === ContactResearchProviderStatus.Empty ||
							progress.status === ContactResearchProviderStatus.Skipped
						"
					>
						−
					</span>
					<AppIcon
						v-else-if="progress.status === ContactResearchProviderStatus.Error"
						:name="IconName.Close"
						:size="12"
					/>
				</span>
				<strong>{{ progress.provider.label }}</strong>
			</div>
			<span class="status-badge" :class="progress.status">
				{{ statusLabel }}<template v-if="progress.result"> · {{ providerDuration }}</template>
			</span>
		</header>
		<div v-if="progress.logs.length || progress.output" class="provider-output">
			<p v-for="log in progress.logs" :key="`${log.timestamp}-${log.message}`">
				{{ formatTime(log.timestamp) }} {{ log.message }}
			</p>
			<pre v-if="progress.output">{{ progress.output }}</pre>
		</div>
		<p class="provider-message">{{ progressMessage }}</p>
	</article>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import {
	ContactResearchProviderProgressStatus,
	ContactResearchProviderStatus,
	type ContactResearchProviderProgress,
} from "@/types/contactResearch"
import { summarizeContactResearchProvider } from "@/utils/contactResearch"

const props = defineProps<{
	progress: ContactResearchProviderProgress
	currentTime: number
}>()

const { t, locale } = useI18n()

const statusLabel = computed(() => t(`contactResearchPage.status.${props.progress.status}`))

const providerDuration = computed(() => {
	if (props.progress.result) {
		return formatDuration(props.progress.result.durationMs)
	}

	if (!props.progress.startedAt || props.progress.status === ContactResearchProviderProgressStatus.Pending) {
		return formatDuration(0)
	}

	return formatDuration(props.currentTime - new Date(props.progress.startedAt).getTime())
})

const progressMessage = computed(() => {
	if (props.progress.result?.error) {
		return props.progress.result.error
	}

	if (
		props.progress.status === ContactResearchProviderStatus.Empty ||
		props.progress.status === ContactResearchProviderStatus.Skipped
	) {
		return t("contactResearchPage.activity.noResultShort")
	}

	if (!props.progress.result) {
		return t("contactResearchPage.activity.running")
	}

	const summary = summarizeContactResearchProvider(props.progress.result.sanitizedContacts)

	const items = [
		countSummary(summary.emails, "emailCount", "emailCountPlural"),
		countSummary(summary.phones, "phoneCount", "phoneCountPlural"),
		countSummary(summary.linkedInProfiles, "linkedInProfileCount", "linkedInProfileCountPlural"),
	].filter(Boolean)

	return items.join(" · ")
})

function countSummary(count: number, singularKey: string, pluralKey: string): string {
	if (count === 0) {
		return ""
	}

	const key = count === 1 ? singularKey : pluralKey

	return t(`contactResearchPage.activity.summary.${key}`, { count })
}

function formatDuration(durationMs: number): string {
	const seconds = Math.max(0, Math.floor(durationMs / 1000))
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = seconds % 60

	if (minutes > 0) {
		return `${minutes}m ${remainingSeconds}s`
	}

	return `${seconds}s`
}

function formatTime(timestamp: string): string {
	return new Intl.DateTimeFormat(locale.value, { timeStyle: "medium" }).format(new Date(timestamp))
}
</script>

<style scoped>
.provider-progress-card {
	min-width: 0;
	padding: 0;
	overflow: hidden;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);

	header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-10);
		padding: var(--space-14) var(--space-16);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
	}
}

.provider-name {
	display: flex;
	align-items: center;
	gap: var(--space-8);
}

.provider-status-icon {
	display: grid;
	place-items: center;
	width: var(--space-18);
	height: var(--space-18);
	color: var(--neutral-600);
	background: var(--neutral-100);
	border-radius: var(--border-radius-base);

	&.pending,
	&.running {
		background: transparent;
		border: 2px solid var(--neutral-200);
		border-right-color: var(--primary-green);
		border-radius: var(--border-radius-full);
		animation: spin 1s linear infinite;
	}

	&.success {
		color: var(--neutral-0);
		background: var(--primary-green);
	}

	&.error {
		color: var(--neutral-0);
		background: var(--accent-warning);
	}
}

.status-badge {
	display: inline-flex;
	align-items: center;
	width: max-content;
	padding: var(--space-3) var(--space-8);
	color: var(--neutral-700);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	white-space: nowrap;
	background: var(--neutral-100);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-full);

	&.success {
		color: var(--primary-green);
		background: color-mix(in srgb, var(--primary-green) 7%, var(--neutral-0));
		border-color: color-mix(in srgb, var(--primary-green) 35%, var(--neutral-0));
	}

	&.empty,
	&.skipped,
	&.error {
		color: var(--accent-warning);
		background: color-mix(in srgb, var(--accent-warning) 7%, white);
		border-color: color-mix(in srgb, var(--accent-warning) 25%, white);
	}
}

.provider-message {
	padding: 0 var(--space-16) var(--space-14);
	margin: var(--space-10) 0 0;
	color: var(--neutral-600);
	font-size: var(--font-size-small);
}

.provider-output {
	max-height: 220px;
	padding: var(--space-12);
	margin: var(--space-14) var(--space-16) 0;
	overflow: auto;
	color: var(--neutral-100);
	font-family: monospace;
	font-size: var(--font-size-xs);
	background: var(--neutral-900);
	border-radius: var(--border-radius-base);

	p,
	pre {
		margin: 0;
		white-space: pre-wrap;
		word-break: break-word;
	}

	pre {
		padding-top: var(--space-10);
		margin-top: var(--space-10);
		color: var(--brand-light-green);
		border-top: var(--border-width-thin) solid var(--neutral-700);
	}
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

@media (max-width: 640px) {
	.provider-progress-card header {
		align-items: flex-start;
		flex-direction: column;
	}
}
</style>
